import React, { useState, useEffect } from 'react';
import { Gauge, Calendar, Ruler, StickyNote, FileText } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FileUploadField from '../ui/FileUploadField';
import { formatDateForAPI } from '../../utils/dateUtils';
import { useUpdateReading } from '../../hooks/useReadings';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const EditReadingModal = ({ isOpen, onClose, reading }) => {
    const [formData, setFormData] = useState({
        value: '',
        unit: '',
        readAt: '',
        notes: '',
        fileUid: null,
    });

    const [isUploading, setIsUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const updateReading = useUpdateReading();
    const { t } = useLanguage();

    useEffect(() => {
        if (reading) {
            setFormData({
                value: reading.value?.toString() || '',
                unit: reading.unit || '',
                readAt: reading.read_at ? reading.read_at.split('T')[0] : '',
                notes: reading.notes || '',
                fileUid: reading.fileUid || null,
            });
        }
    }, [reading]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.value) {
            newErrors.value = t(KEYS.READING_FIELD_VALUE_REQUIRED);
        } else if (isNaN(parseFloat(formData.value)) || parseFloat(formData.value) < 0) {
            newErrors.value = t(KEYS.READING_FIELD_VALUE_REQUIRED);
        }

        if (!formData.readAt) {
            newErrors.readAt = t(KEYS.READING_FIELD_DATE_REQUIRED);
        }

        if (formData.unit && formData.unit.length > 50) {
            newErrors.unit = 'Maximum 50 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const data = {
                value: formData.value ? parseFloat(formData.value) : undefined,
                read_at: formData.readAt ? formatDateForAPI(formData.readAt) : undefined,
                unit: formData.unit || undefined,
                notes: formData.notes || undefined,
                file_uid: formData.fileUid ?? undefined,
            };

            await updateReading.mutateAsync({ readingUid: reading.uid, data });

            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.READING_EDIT_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.READING_EDIT_TITLE)}
            subtitle="Update reading details"
            size="medium"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button
                        variant="primary"
                        size="medium"
                        onClick={handleSubmit}
                        disabled={isSubmitting || isUploading}
                    >
                        {isSubmitting ? t(KEYS.READING_EDIT_SUBMITTING) : t(KEYS.READING_EDIT_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm mb-4">
                    {submitError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Service (read-only) */}
                {reading?.serviceName && (
                    <div className="space-y-1">
                        <label className="label-base flex items-center gap-1.5">
                            <FileText className="w-4 h-4" />
                            Service
                        </label>
                        <div className="input-base bg-surface-overlay text-text-muted cursor-not-allowed">
                            {reading.serviceName}
                        </div>
                    </div>
                )}

                {/* Value */}
                <div className="space-y-1">
                    <label htmlFor="value" className="label-base flex items-center gap-1.5">
                        <Gauge className="w-4 h-4" />
                        Value <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="number"
                        id="value"
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        className={`input-base ${errors.value ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., 1234.56"
                        step="0.01"
                        min="0"
                        disabled={isSubmitting}
                    />
                    {errors.value && (
                        <span className="text-xs text-red-500 mt-1">{errors.value}</span>
                    )}
                </div>

                {/* Unit */}
                <div className="space-y-1">
                    <label htmlFor="unit" className="label-base flex items-center gap-1.5">
                        <Ruler className="w-4 h-4" />
                        Unit
                    </label>
                    <input
                        type="text"
                        id="unit"
                        name="unit"
                        value={formData.unit}
                        onChange={handleChange}
                        className={`input-base ${errors.unit ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., kWh, m³, L"
                        maxLength={50}
                        disabled={isSubmitting}
                    />
                    {errors.unit && (
                        <span className="text-xs text-red-500 mt-1">{errors.unit}</span>
                    )}
                </div>

                {/* Read At */}
                <div className="space-y-1">
                    <label htmlFor="readAt" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Read Date <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        id="readAt"
                        name="readAt"
                        value={formData.readAt}
                        onChange={handleChange}
                        className={`input-base ${errors.readAt ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.readAt && (
                        <span className="text-xs text-red-500 mt-1">{errors.readAt}</span>
                    )}
                </div>

                {/* Notes */}
                <div className="space-y-1">
                    <label htmlFor="notes" className="label-base flex items-center gap-1.5">
                        <StickyNote className="w-4 h-4" />
                        Notes
                    </label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="input-base resize-y"
                        placeholder="Additional notes (optional)"
                        rows={3}
                        disabled={isSubmitting}
                    />
                </div>

                {/* File Upload */}
                {reading?.serviceUid && (
                    <FileUploadField
                        label="Meter Photo / Document"
                        serviceUid={reading.serviceUid}
                        value={formData.fileUid}
                        onChange={(uid) => setFormData((prev) => ({ ...prev, fileUid: uid }))}
                        onUploadingChange={(uploading) => setIsUploading(uploading)}
                        disabled={isSubmitting}
                    />
                )}
            </form>
        </Modal>
    );
};

export default EditReadingModal;
